/**
 * « LA VERSION APPLICABLE D'ABORD » — module PUR, MIROIR de
 * `backend/src/version-en-cours.ts` (06/10/2026).
 *
 * Demande de la RQ : « quand un document est en cours de révision, au premier
 * abord je voudrais voir la version en cours, avec une mention qu'une nouvelle
 * version est en cours — et je peux cliquer dessus pour la voir si mes droits
 * me le permettent ; sauf si j'y accède depuis le menu "à rédiger / à vérifier
 * / à approuver". »
 *
 * ⚠️ Le serveur fait foi : il refuse l'aperçu du candidat et l'ouverture du
 * fichier à qui n'y a pas droit (`apercu.ts`, `office.ts`, `verrou.ts`). Ce
 * module ne sert qu'à ce que l'écran ne PROPOSE pas ce que le serveur refusera —
 * et à écrire la phrase du refus AVANT le clic, pas après.
 *
 * Le test `backend/tests/version-en-cours-miroir.test.js` vérifie que les deux
 * fichiers ne divergent pas.
 */
import type { GedDocument, GedVersion } from './types';

type Doc = Pick<GedDocument, 'statut' | 'versionCourante' | 'versions' | 'redacteur' | 'redacteurs' | 'verificateur' | 'approbateur'>;

/** L'étape du circuit qui travaille sur la version candidate. */
export type EtapeEnCours = 'redaction' | 'verification' | 'approbation';

/** La version APPLICABLE : mise en vigueur et pas encore remplacée. */
export function versionApplicable(versions: GedVersion[] | null | undefined): GedVersion | null {
  return (versions || []).find((v) => v.dateVigueur && !v.obsolete) || null;
}

/** La version CANDIDATE : en travail ou en revue — ni en vigueur, ni obsolète. */
export function versionCandidate(versions: GedVersion[] | null | undefined): GedVersion | null {
  return (versions || []).find((v) => !v.dateVigueur && !v.obsolete) || null;
}

/**
 * Une révision est EN COURS : une version s'applique, une autre la remplacera.
 *
 * ⚠️ Ne pas remplacer par `statut === 'enrevision'` : une révision soumise
 * porte `averifier` ou `aapprouver` alors que l'ancienne version s'applique
 * toujours.
 */
export function revisionEnCours(doc: Doc | null | undefined): boolean {
  if (!doc) return false;
  const cand = versionCandidate(doc.versions);
  if (!versionApplicable(doc.versions) || !cand) return false;
  /* ⚠️ REPRISE APRÈS RETRAIT D'URGENCE : ce n'est PAS une révision. L'ancienne
     version garde `dateVigueur` sans être obsolète — elle a donc l'apparence
     d'une version applicable, alors que ses destinataires ont été prévenus
     « ne pas appliquer ». Sans cette garde, la fiche l'aurait présentée comme
     applicable et imprimable. Drapeau posé par `reprendreRetrait` (serveur). */
  if (cand.reprisApresRetrait) return false;
  return true;
}

/** Qui travaille sur le candidat, d'après le statut du document. */
export function etapeEnCours(statut: string | null | undefined): EtapeEnCours | null {
  if (statut === 'brouillon' || statut === 'enrevision') return 'redaction';
  if (statut === 'averifier') return 'verification';
  if (statut === 'aapprouver') return 'approbation';
  return null;
}

/** Ce que l'étape fait au document, en toutes lettres. */
export function libelleEtape(etape: EtapeEnCours | null): string {
  if (etape === 'redaction') return 'en cours de rédaction';
  if (etape === 'verification') return 'en cours de vérification';
  if (etape === 'approbation') return 'en attente d’approbation';
  return 'en cours';
}

/** Le numéro de version que le FICHIER courant contient aujourd'hui. */
export function versionDuFichier(doc: Doc | null | undefined): string | null {
  if (!doc) return null;
  const cand = versionCandidate(doc.versions);
  if (cand) return String(cand.num || doc.versionCourante || '') || null;
  const appl = versionApplicable(doc.versions);
  return String(appl?.num || doc.versionCourante || '') || null;
}

/** Qui a RÉDIGÉ le candidat — même base que `redacteursDe` (serveur et écran). */
export function redacteursDuCandidat(doc: Doc | null | undefined): string[] {
  const cand = versionCandidate(doc?.versions);
  return Array.from(new Set([
    ...(cand?.contributeurs || []),
    cand?.redacteur,
    doc?.redacteur,
    ...(doc?.redacteurs || []),
  ].filter(Boolean) as string[]));
}

export const REFUS_VERSION_EN_COURS =
  'La version en cours de rédaction ne se consulte que par ses rédacteurs, ses signataires désignés et le responsable qualité. '
  + 'La version applicable, elle, reste ouverte à tous — c’est celle qui s’applique.';

/**
 * QUI PEUT OUVRIR LA VERSION EN COURS (arbitrage RQ, 06/10/2026) : rédacteurs
 * affectés, signataires désignés, RQ.
 *
 * ⚠️ Plus LARGE que `attendDeMoi` à dessein : LIRE le travail en cours n'est
 * pas le SIGNER. Un vérificateur désigné que la séparation des tâches empêche
 * de signer doit pouvoir le lire — c'est ce qui lui permet de constater qu'il
 * faut désigner quelqu'un d'autre.
 */
export function accesVersionEnCours(
  doc: Doc | null | undefined,
  moi: { key: string; roles: string[] },
): { ouvert: boolean; refus: string } {
  const roles = moi.roles || [];
  if (!moi.key) return { ouvert: false, refus: REFUS_VERSION_EN_COURS };
  if (roles.includes('rq')) return { ouvert: true, refus: '' };
  if (redacteursDuCandidat(doc).includes(moi.key)) return { ouvert: true, refus: '' };
  if (doc?.verificateur === moi.key || doc?.approbateur === moi.key) return { ouvert: true, refus: '' };
  // Aucun signataire désigné à l'étape en cours : le rôle suffit, et là seulement.
  const etape = etapeEnCours(doc?.statut);
  if (etape === 'verification' && !doc?.verificateur && roles.includes('verificateur')) return { ouvert: true, refus: '' };
  if (etape === 'approbation' && !doc?.approbateur && roles.includes('approbateur')) return { ouvert: true, refus: '' };
  return { ouvert: false, refus: REFUS_VERSION_EN_COURS };
}

/**
 * LE FICHIER SOURCE D'UN DOCUMENT EN VIGUEUR (décision RQ, 20/10/2026) —
 * miroir de `sourceEnVigueur` / `accesSourceEnVigueur` (serveur, qui fait foi).
 * En vigueur et rien en rédaction : l'onglet Fichier et le téléchargement de
 * la source sont au RQ SEUL — même qui l'a rédigée n'y a plus accès, sa
 * mission est finie. Les autres lisent l'Aperçu.
 */
export function sourceEnVigueur(doc: Doc | null | undefined): boolean {
  return !!versionApplicable(doc?.versions) && !versionCandidate(doc?.versions);
}

export function accesSourceEnVigueur(
  doc: Doc | null | undefined,
  moi: { roles: string[] },
): { voir: boolean; telecharger: boolean } {
  const ouvert = !sourceEnVigueur(doc) || (moi.roles || []).includes('rq');
  return { voir: ouvert, telecharger: ouvert };
}
